"use client";

import { AnimatedSectionHeader } from "@/components/typography/AnimatedSectionHeader";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import {
  resolveAboutValueIcon,
  type AboutValueIconId,
} from "@/lib/about-value-icons";
import type { AboutValuesContent } from "@/lib/api-types";
import { cn } from "@/lib/utils";

interface AboutValuesSectionProps {
  values: AboutValuesContent;
}

export function AboutValuesSection({ values }: AboutValuesSectionProps) {
  return (
    <section className="site-section bg-[var(--site-surface)]">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <Reveal>
          <AnimatedSectionHeader
            kineticTrigger="mount"
            title="Our Values"
            subtitle="The spirit behind Abhaya Hastham"
          />
        </Reveal>
        <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {values.items.map((v) => {
            const Icon = resolveAboutValueIcon(v.icon as AboutValueIconId);
            return (
              <StaggerItem key={v.title}>
                <article
                  className={cn(
                    "site-card group h-full p-5 text-center",
                    "transition-all duration-500 ease-[var(--ease-out-expo)]",
                    "hover:-translate-y-1 hover:shadow-lg"
                  )}
                >
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--site-accent)]/15 text-[var(--site-accent)] transition-colors group-hover:bg-[var(--site-accent)]/25">
                    <Icon size={22} aria-hidden />
                  </div>
                  <h4 className="mb-2 font-semibold text-[var(--site-text)]">
                    {v.title}
                  </h4>
                  <p className="text-sm leading-relaxed text-[var(--site-muted)]">
                    {v.description}
                  </p>
                </article>
              </StaggerItem>
            );
          })}
        </Stagger>
        <Reveal className="mt-10 text-center">
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-[var(--site-muted)]">
            {values.footer}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
